'use client';

import { useState } from 'react';
import {
    Button,
    Modal,
    Link,
    Tooltip,
    Logo,
    RatingStars,
    Box,
    Typography,
    CircularProgress,
    Input,
    DropdownMenu,
} from '@repo/ui';
import StarIcon from '@mui/icons-material/Star';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import { addCourseRating } from "@/redux/course"
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { getCourseProgress } from '@/utils/course-utils';
import { useInteractiveCourseStore } from '@/store/mutable-data/course/CourseStoreContext';
import { useUserStore } from '@/store/mutable-data/user/UserStoreContext';
import { logoutUser } from '@/store/mutable-data/user/actions';
import RedirectBackLinkWrapper from '@/components/molecules/RedirectBackLinkWrapper';
import styles from './LectureTopMenu.module.scss';

// import { useAppDispatch, useAppSelector } from "@repo/utils/hooksUtils"
// import CourseCertificate from "../CourseCertificate"
const CourseCertificate = dynamic(() => import('../CourseCertificate'), {
    loading: () => (
        <Button margin="1rem 0 0 0" loading>
            Download Certificate
        </Button>
    ),
});

const RightSide = ({ course }) => {
    const { user } = useUserStore();
    const { courseUserAccess } = useInteractiveCourseStore();

    const { lecturesFinished, totalLecturesCount, progress } = getCourseProgress(
        course,
        courseUserAccess.data,
    );

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [showLeaveRatingModal, setShowLeaveRatingModal] = useState(false);
    const [showLeaveRatingDescriptionModal, setShowLeaveRatingDescriptionModal] = useState(false);

    const [rating, setRating] = useState();
    const [reviewLoading, setReviewLoading] = useState(false);

    const changeRating = (newRating) => {
        setShowLeaveRatingModal(false);
        setShowLeaveRatingDescriptionModal(true);
        setRating(newRating);
    };

    // const dispatch = useAppDispatch()
    const handleReviewSubmit = async (event) => {
        event.preventDefault();
        setReviewLoading(true);

        // const data = new FormData(event.currentTarget)

        // await dispatch(
        //   addCourseRating({
        //     courseId: course.id,
        //     data: {
        //       rating,
        //       description: data.get("description"),
        //     },
        //   })
        // )

        setShowLeaveRatingDescriptionModal(false);
        setReviewLoading(false);
    };

    return (
        <>
            <div className={styles['top-menu-right-menu']}>
                {course.myCourseReview &&
                    !course.myCourseReview?.loading &&
                    !course.myCourseReview?.id && (
                        <div
                            role="button"
                            aria-label="Leave rating"
                            className={styles['top-menu-right-menu-leave-rating']}
                            onClick={() => {
                                setShowLeaveRatingModal(true);
                            }}
                        >
                            <StarIcon className={styles['top-menu-star']} />
                            Leave rating
                        </div>
                    )}

                <div className={styles['top-menu-right-menu-your-progress']}>
                    <Box
                        className={styles['progressbar']}
                        style={{ position: 'relative' }}
                        onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                        }}
                    >
                        <CircularProgress value={progress} />
                        <Typography margin="0 0 0 0.5rem">Your progress</Typography>
                        <ExpandMoreIcon />

                        <DropdownMenu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={() => {
                                setAnchorEl(null);
                            }}
                        >
                            <div className={styles['top-menu-progressbar-toolbar']}>
                                {lecturesFinished} of {totalLecturesCount} completed
                                <div>
                                    {lecturesFinished === totalLecturesCount ? (
                                        <CourseCertificate
                                            course={course}
                                            userFullName={user.fullName}
                                        />
                                    ) : (
                                        <Tooltip
                                            title={
                                                'You must finish all lessons to download your certificate'
                                            }
                                        >
                                            <div>
                                                <Button margin="1rem 0 0 0" disabled={true}>
                                                    Download Certificate
                                                </Button>
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        </DropdownMenu>
                    </Box>

                    <Button
                        margin="0 0 0 1rem"
                        onClick={() => {
                            window.$crisp.push(['do', 'chat:show']);
                            window.$crisp.push(['do', 'chat:open']);
                        }}
                        startIcon={<ContactSupportIcon />}
                    >
                        Need Help?
                    </Button>
                </div>
                {/* <Button className={styles["top-menu-right-menu-share"]} > Share</Button> */}
            </div>

            <Modal
                open={showLeaveRatingModal}
                onClose={() => {
                    setShowLeaveRatingModal(false);
                    setRating();
                    setReviewLoading(false);
                }}
            >
                <Box className={styles['leave-rating-modal']}>
                    <h2>How would you rate this course?</h2>
                    <p>Select Rating</p>
                    <RatingStars value={Number(rating)} onChange={changeRating} size={32} />
                </Box>
            </Modal>

            <Modal
                open={showLeaveRatingDescriptionModal}
                onClose={() => {
                    setShowLeaveRatingDescriptionModal(false);
                    setRating();
                    setReviewLoading(false);
                }}
            >
                <Box
                    className={styles['leave-rating-modal']}
                    component="form"
                    onSubmit={handleReviewSubmit}
                >
                    <h2>Why did you leave this rating?</h2>
                    <RatingStars value={Number(rating)} onChange={setRating} size={32} />
                    <Input
                        id="description"
                        name="description"
                        multiline
                        minRows={5}
                        maxRows={5}
                        placeholder="Tell us about your own personal experience taking this course. Was it a good match for you?"
                        className={styles['rating-description']}
                    />
                    <Button type="submit" loading={reviewLoading}>
                        Save
                    </Button>
                </Box>
            </Modal>
        </>
    );
};

const LectureTopMenu = ({ course = undefined, title = '' }) => {
    const { user, setUserState } = useUserStore();
    const loggedIn = Boolean(user.id);

    const logout = () => {
        logoutUser(user.id, setUserState);
    };

    const { push } = useRouter();

    const {
        showSyllabus: { setShowSyllabus },
    } = useInteractiveCourseStore();

    return (
        <div>
            <div className={styles['top-menu-container']}>
                <div className={styles['top-menu-container-inner']}>
                    <div className={styles['top-menu-container-content']}>
                        <div className={styles['top-menu-left-side']}>
                            <Link to={'/'}>
                                <Logo Image={Image} />
                            </Link>

                            {title ? (
                                <Typography variant="h1" className={styles['top-menu-title']}>
                                    {title}
                                </Typography>
                            ) : (
                                <>
                                    <Button
                                        onClick={() => {
                                            push('/');
                                        }}
                                        className={styles['top-menu-items']}
                                        variant="text"
                                        style={{ color: '#fff', fontWeight: 400 }}
                                    >
                                        My Home
                                    </Button>
                                    {course && (
                                        <Button
                                            onClick={() => {
                                                setShowSyllabus(true);
                                            }}
                                            variant="text"
                                            style={{ color: '#fff', fontWeight: 400 }}
                                        >
                                            Syllabus
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                        <div>
                            {loggedIn ? (
                                course ? (
                                    <RightSide course={course} />
                                ) : (
                                    <Button onClick={logout}>Logout</Button>
                                )
                            ) : (
                                <Box display="flex">
                                    <RedirectBackLinkWrapper url="/login">
                                        <Button>Login</Button>
                                    </RedirectBackLinkWrapper>

                                    <RedirectBackLinkWrapper url="/register">
                                        <Button color="secondary" margin="0 0 0 0.5rem">
                                            Register
                                        </Button>
                                    </RedirectBackLinkWrapper>
                                </Box>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ height: 'var(--top-menu-height)' }} />
        </div>
    );
};

export default LectureTopMenu;
